import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { Resume } from 'packages/types'
import { useI18n } from 'builder/hooks/useI18n'
import { Icon24, Icon20 } from 'builder/components/Icon'
import {
  ColorChangeHandler,
  FontChangeHandler,
  LayoutFormatChangeHandler,
  SettingsFieldChangeHandler,
  SizeChangeHandler,
  SpacingChangeHandler,
  TemplateChangeHandler,
} from 'builder/modules/resumeEditor'
import {
  Container,
  TabContainer,
  ContentContainer,
  CustomTab,
  TabLabelContainer,
  IconContainer,
  CustomTabs,
  ContentScrollWrapper,
} from './styles'
import { TabType, Tab } from './types'
import { TemplateSelection } from './components/TemplateSelection/TemplateSelection'
import { TextCustomization } from './components/TextCustomization/TextCustomization'
import { LayoutCustomization } from './components/LayoutCustomization/LayoutCustomization'

interface ResumeSidebarProps {
  resume: Resume
  updateTemplate: TemplateChangeHandler
  handleColorSelect: ColorChangeHandler
  handleFontChange?: FontChangeHandler
  handleChangeLayoutFormat?: LayoutFormatChangeHandler
  handleSpacingChange?: SpacingChangeHandler
  handleSizeChange?: SizeChangeHandler
  handleSettingsFieldChange?: SettingsFieldChangeHandler
}

export const ResumeSidebar = (props: ResumeSidebarProps) => {
  const {
    resume,
    updateTemplate,
    handleColorSelect,
    handleFontChange,
    handleChangeLayoutFormat,
    handleSizeChange,
    handleSpacingChange,
    handleSettingsFieldChange,
  } = props
  const { i18n } = useI18n()
  const [selectedTab, setSelectedTab] = useState<TabType>(TabType.templates)
  const containerRef = useRef<HTMLDivElement>(null)

  const tabs: Tab[] = useMemo(
    () => [
      {
        label: (
          <TabLabelContainer>
            <IconContainer>
              <Icon24.Grid />
            </IconContainer>
            {i18n.t('builder.resume_editor.document_customization.sidebar.templates_tab.header')}
          </TabLabelContainer>
        ),
        id: TabType.templates,
        content: (
          <TemplateSelection
            {...{
              currentDocument: resume,
              updateTemplate,
              handleColorSelect,
            }}
          />
        ),
      },
      {
        label: (
          <TabLabelContainer>
            <IconContainer>
              <Icon24.FontSize />
            </IconContainer>
            {i18n.t('builder.resume_editor.document_customization.sidebar.text_tab.header')}
          </TabLabelContainer>
        ),
        id: TabType.text,
        content: (
          <ContentScrollWrapper className="has-overflow-space">
            <TextCustomization
              {...{
                resume,
                handleFontChange,
                handleSizeChange,
                handleSpacingChange,
                handleSettingsFieldChange,
              }}
            />
          </ContentScrollWrapper>
        ),
      },
      {
        label: (
          <TabLabelContainer>
            <IconContainer>
              <Icon20.Documents />
            </IconContainer>
            {i18n.t('builder.resume_editor.document_customization.sidebar.layout_tab.header')}
          </TabLabelContainer>
        ),
        id: TabType.layout,
        content: (
          <ContentScrollWrapper>
            <LayoutCustomization
              {...{ resume, handleChangeLayoutFormat, handleSettingsFieldChange }}
            />
          </ContentScrollWrapper>
        ),
      },
    ],
    [
      i18n,
      updateTemplate,
      handleColorSelect,
      resume,
      handleFontChange,
      handleChangeLayoutFormat,
      handleSizeChange,
      handleSpacingChange,
      handleSettingsFieldChange,
    ],
  )

  const selectedTabContent = useMemo(
    () => tabs.find(tab => tab.id === selectedTab)?.content,
    [tabs, selectedTab],
  )

  const handleTabClick = useCallback((id: TabType) => {
    setSelectedTab(id)
    trackInternalEvent('change_customization_tab', {
      label: id,
    })
  }, [])

  const tabElements = useMemo(
    () =>
      tabs.map(tab => (
        <CustomTab
          key={tab.id}
          isActive={tab.id === selectedTab}
          onClick={() => handleTabClick(tab.id)}
          data-testid={`customization-sidebar-tab-${tab.id}`}
        >
          {tab.label}
        </CustomTab>
      )),
    [tabs, selectedTab, handleTabClick],
  )

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    disableBodyScroll(element)
    return () => {
      enableBodyScroll(element)
    }
  }, [])

  return (
    <Container ref={containerRef}>
      <TabContainer>
        <CustomTabs>{tabElements}</CustomTabs>
      </TabContainer>
      <ContentContainer>{selectedTabContent}</ContentContainer>
    </Container>
  )
}
